import type { MetricTile, MetricTrend, MetricTrendSentiment } from '@core/tellent-assistant-types';

import { shortenIsoDates } from './shorten-iso-dates';

export type TrendArrow = 'up' | 'down' | 'right';

export interface TrendView {
  /** `null` for absolute deltas — those carry a `+`/`-` sign instead of an arrow. */
  readonly arrow: TrendArrow | null;
  readonly change: string;
  readonly period: string;
  readonly sentiment: MetricTrendSentiment;
}

export interface TileView {
  readonly label: string;
  readonly value: string;
  readonly trend: TrendView | null;
  readonly note: string | null;
}

const LEADING_SIGN = /^[+\-−–]\s*/;

const stripSign = (change: string): string => change.trim().replace(LEADING_SIGN, '');

const signed = (change: string, direction: 'up' | 'down'): string =>
  `${direction === 'up' ? '+' : '-'}${stripSign(change)}`;

/**
 * A trend shows EITHER an arrow (relative change: "↑ 28%") OR a signed number
 * ("+9"), never both — a design invariant, so it is enforced here rather than
 * left to whatever the agent passed. Relative changes are the ones carrying a
 * `%`; everything else reads as an absolute delta.
 */
function toTrendView(trend: MetricTrend): TrendView {
  const base = { period: shortenIsoDates(trend.period), sentiment: trend.sentiment };

  if (trend.direction === 'flat') {
    return { ...base, arrow: 'right', change: stripSign(trend.change) };
  }

  if (trend.change.includes('%')) {
    return { ...base, arrow: trend.direction, change: stripSign(trend.change) };
  }

  return { ...base, arrow: null, change: signed(trend.change, trend.direction) };
}

export function toMetricTileView(tile: MetricTile): TileView {
  const footnote = tile.footnote;
  return {
    label: tile.label,
    value: tile.value,
    trend: footnote?.kind === 'trend' ? toTrendView(footnote) : null,
    note: footnote?.kind === 'note' ? shortenIsoDates(footnote.text) : null,
  };
}
